/**
 * The name half of a cloud Environment row (FOR-36371).
 *
 * The name IS the affordance: clicking it opens the field. A projectless
 * Environment has no overflow menu, so hanging rename off one would have left
 * exactly the Environments the Add flow creates unnameable.
 */
import { useId } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  ENVIRONMENT_NAME_MAX_LENGTH,
  type CloudEnvironmentRenameController,
} from "./useCloudEnvironmentRename";

export { ENVIRONMENT_NAME_TAKEN_MESSAGE } from "./useCloudEnvironmentRename";

export const RENAME_ENVIRONMENT_LABEL = "Rename this environment";
export const SAVE_ENVIRONMENT_NAME_LABEL = "Save environment name";
const CANCEL_ENVIRONMENT_NAME_LABEL = "Cancel renaming this environment";

/**
 * The row's name, as a button that opens the field.
 *
 * Styled as the row title rather than as a control: `SettingsRow`'s title line,
 * with the hover response the surrounding row already promises. A button that
 * looked like a button would put a second boxed thing in a cell the settings
 * standard keeps to one line of text.
 */
export function CloudEnvironmentNameTrigger({
  name,
  onStart,
}: {
  readonly name: string;
  readonly onStart: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={RENAME_ENVIRONMENT_LABEL}
      className="min-w-0 truncate rounded-[var(--control-radius)] text-left text-sm max-md:inline-flex max-md:min-h-touch max-md:items-center font-medium tracking-[-0.005em] text-foreground underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      onClick={onStart}
    >
      {name}
    </button>
  );
}

/**
 * The open rename field.
 *
 * `derivedName` is the PLACEHOLDER, never the value: an empty field means "go
 * back to deriving from the owner", and pre-filling the derived text would
 * write it back as a chosen name on the next save.
 *
 * Enter saves and Escape cancels, because the row keeps its Save and Cancel in
 * the actions cell and a keyboard user should not have to travel there.
 */
export function CloudEnvironmentNameField({
  derivedName,
  rename,
}: {
  readonly derivedName: string;
  readonly rename: CloudEnvironmentRenameController;
}) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  return (
    <div className="min-w-0 space-y-1">
      <label className="sr-only" htmlFor={inputId}>
        Environment name
      </label>
      <Input
        id={inputId}
        autoComplete="off"
        autoFocus
        aria-describedby={rename.error === null ? undefined : errorId}
        aria-invalid={rename.error === null ? undefined : true}
        disabled={rename.isSaving}
        maxLength={ENVIRONMENT_NAME_MAX_LENGTH}
        placeholder={derivedName}
        value={rename.draft}
        onChange={(event) => rename.setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void rename.submit();
            return;
          }
          if (event.key === "Escape") {
            event.preventDefault();
            rename.cancel();
          }
        }}
      />
      {rename.error === null ? (
        <p className="text-[13px] leading-[1.45] text-muted-foreground/80">
          Leave it empty to go back to {derivedName}.
        </p>
      ) : (
        <p
          id={errorId}
          role="alert"
          className="text-[13px] leading-[1.45] text-destructive-foreground"
        >
          {rename.error}
        </p>
      )}
    </div>
  );
}

/** Save and Cancel, in the row's own actions cell while the field is open. */
export function CloudEnvironmentNameActions({
  rename,
}: {
  readonly rename: CloudEnvironmentRenameController;
}) {
  return (
    <>
      <Button
        size="xs"
        variant="outline"
        aria-label={SAVE_ENVIRONMENT_NAME_LABEL}
        className="max-md:min-h-touch max-md:rounded-xl"
        disabled={rename.isSaving}
        onClick={() => void rename.submit()}
      >
        {rename.isSaving ? "Saving" : "Save"}
      </Button>
      <Button
        size="xs"
        variant="outline"
        aria-label={CANCEL_ENVIRONMENT_NAME_LABEL}
        className="max-md:min-h-touch max-md:rounded-xl"
        disabled={rename.isSaving}
        onClick={rename.cancel}
      >
        Cancel
      </Button>
    </>
  );
}
